import React, { useEffect, useState, useCallback } from "react";
import MainTemplate from "../../templates/MainTemplate";
import "./style.css";
import { 
  Grid, 
  Button, 
  Snackbar, 
  Box, 
  Typography, 
  Tooltip, 
  Tab, 
  Tabs,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  MenuItem,
  FormControl,
  Select,
  Stack,
  Pagination,
  InputBase,
  IconButton,
  CircularProgress
} from "@mui/material";
import {
  getSummary,
  getSurveys,
  getSurveyors,
  updateStatus,
  deleteSurvey,
  restoreSurvey,
} from "../../../api/services/services";
import { getImageUrl } from "../../../api/config/apiConfig";
import { sendSuperAdminVerificationForEdit } from "../PrebidAgreement/creation/service";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useNavigate, useLocation } from "react-router-dom";
import AssignSurveyorModal from "../../molecules/AssignSurveyorModal";
import SuccessModal from "../../molecules/SuccessModal";
import MuiAlert from "@mui/material/Alert";
import RejectModal from "../../molecules/RejectModal";
import SuperAdminVerifationModal from "../../molecules/SuperAdminVerifationModal";

const Dashboard = ({ defaultRecycleBin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isRecycleRoute = defaultRecycleBin || location.pathname === "/recycle-bin";

  const [isAll, setIsAll] = useState(true);
  const [status, setStatus] = useState("");
  const [isRecycleBin, setIsRecycleBin] = useState(isRecycleRoute);

  useEffect(() => {
    if (location.pathname === "/recycle-bin") {
      setIsRecycleBin(true);
    } else if (location.pathname === "/" || location.pathname === "/surveys") {
      setIsRecycleBin(false);
    }
  }, [location.pathname]);

  const dataStored = localStorage.getItem("shipmentsRecords");

  const [data, setData] = useState(dataStored ? JSON.parse(dataStored) : []);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [idToSend, setIdToSend] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const [surveyorsList, setSurveyorsList] = useState([]);
  const [selectedSurveyor, setSelectedSurveyor] = useState("ALL");

  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState(null);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [reject, setReject] = useState(false);

  const [successHead, setSuccessHead] = useState("");
  const [successSub, setSuccessSub] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isEditLoading, setIsEditLoading] = useState(false);

  const [totalCount, setTotalCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [assignedCount, setAssignedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [inprogressCount, setInProgressCount] = useState(0);

  const [search, setSearch] = useState("");
  
  // Date Input State (UI only)
  const [fromDateInput, setFromDateInput] = useState("");
  const [toDateInput, setToDateInput] = useState("");

  // Applied Date State (API trigger)
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  const handleApplyFilter = () => {
    let from = fromDateInput;
    let to = toDateInput;
    
    // Convert to Unix Timestamp (Seconds) for Backend
    let fromTs = "";
    let toTs = "";

    if (from) {
       const dateObj = new Date(from);
       dateObj.setHours(0, 0, 0, 0); // Start of day
       fromTs = Math.floor(dateObj.getTime() / 1000);
    }
    
    if (to) {
       const dateObj = new Date(to);
       dateObj.setHours(23, 59, 59, 999); // End of day
       toTs = Math.floor(dateObj.getTime() / 1000);
    }

    setAppliedFromDate(fromTs);
    setAppliedToDate(toTs);
  };

  const handleClearFilter = () => {
    setFromDateInput("");
    setToDateInput("");
    setAppliedFromDate("");
    setAppliedToDate("");
    setSelectedSurveyor("ALL");
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value);
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  const getAllShipments = useCallback(() => {
    setIsLoading(true);
    // Pass applied date and surveyor filters to API
    getSurveys(
      isAll,
      status,
      currentPage,
      itemsPerPage,
      search,
      isRecycleBin,
      appliedFromDate,
      appliedToDate,
      selectedSurveyor !== "ALL" ? selectedSurveyor : null
    )
      .then((response) => {
        setIsLoading(false);
        const shipmentList = response?.data?.data?.surveyList || [];
        setData(shipmentList);
        localStorage.setItem("shipmentsRecords", JSON.stringify(shipmentList));
        setTotalPages(Math.ceil(response?.data?.data?.totalRecords / itemsPerPage));
      })
      .catch((error) => {
        setIsLoading(false);
        if (error?.response?.data?.statusCode === 401) {
          localStorage.clear();
          window.location.pathname = "/login";
        }
      });
  }, [isAll, status, currentPage, itemsPerPage, search, isRecycleBin, appliedFromDate, appliedToDate, selectedSurveyor]);

  const getStatusCount = useCallback(() => {
    getSummary()
      .then((response) => {
        const stats = response?.data?.data || [];
        let count = 0;
        let n = 0, a = 0, r = 0, ip = 0;
        stats.forEach((item) => {
          if (item.status === "new") n = item.survey_count;
          else if (item.status === "assigned") a = item.survey_count;
          else if (item.status === "rejected") r = item.survey_count;
          else if (item.status === "inprogress") ip = item.survey_count;
          count += item.survey_count;
        });
        setNewCount(n);
        setAssignedCount(a);
        setRejectedCount(r);
        setInProgressCount(ip);
        setTotalCount(count);
      })
      .catch((error) => {
        if (error?.response?.data?.statusCode === 401) {
          localStorage.clear();
          window.location.pathname = "/login";
        }
      });
  }, []);

  useEffect(() => {
    getSurveyors(1, 100, true, "")
      .then((res) => {
        const list = res?.data?.data?.surveyers || [];
        setSurveyorsList(list);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getAllShipments();
    getStatusCount();
  }, [getAllShipments, getStatusCount]);

  const handleStatus = (event, id) => {
    setIdToSend(id);
    const newStatus = event.target.value;
    if (newStatus === "assigned") {
      setShowAssign(true);
    } else if (newStatus === "rejected") {
      setReject(true);
    } else if (newStatus === "sendquotation") {
      navigate(`/survey/quotation-form/${id}/be5690e1-df05-4791-a214-2235c757f0f3`);
    } else {
      updateStatus(id, { status: newStatus })
        .then(() => {
          setSnackbarMessage("Shipment status updated successfully!");
          setOpenSnackbar(true);
          getAllShipments();
        })
        .catch((error) => {
          setSnackbarMessage(error?.response?.data?.message || "Error updating status");
          setOpenSnackbar(true);
        });
    }
  };

  const initiateDelete = (row) => {
    setPendingAction({ type: 'delete', id: row.uuid });
    setIsEditLoading(true);
    sendSuperAdminVerificationForEdit()
      .then(() => {
        setIsVerified(true);
        setIsEditLoading(false);
      })
      .catch((error) => {
        setSnackbarMessage(error?.response?.data?.message || "Error sending verification code");
        setOpenSnackbar(true);
        setIsEditLoading(false);
      });
  };

  const handleDeleteSuccess = () => {
    if (pendingAction?.type === 'delete') {
      deleteSurvey(pendingAction.id)
        .then(() => {
          setSnackbarMessage("Shipment moved to recycle bin successfully!");
          setOpenSnackbar(true);
          getAllShipments();
          getStatusCount();
          setIsVerified(false);
          setPendingAction(null);
        })
        .catch((error) => {
          setSnackbarMessage(error?.response?.data?.message || "Error deleting shipment");
          setOpenSnackbar(true);
        });
    }
  };

  const handleRestore = (id) => {
    restoreSurvey(id)
      .then(() => {
        setSnackbarMessage("Shipment restored successfully!");
        setOpenSnackbar(true);
        getAllShipments();
        getStatusCount();
      })
      .catch((error) => {
        setSnackbarMessage(error?.response?.data?.message || "Error restoring shipment");
        setOpenSnackbar(true);
      });
  };

  const convertDate = (unixTimestamp) => {
    if (!unixTimestamp) return "-";
    const date = new Date(unixTimestamp * 1000);
    return date.toISOString().split('T')[0];
  };

  const goToMoveCheckList = (val) => {
    if (val?.status === "incomplete_info")
      navigate(`/survey/incomplete/${val?.uuid}/new`);
    else navigate(`/survey/move-checklist/${val?.uuid}/${val?.info || '0'}`);
  };

  const getStatusClass = (status) => {
    if (!status) return "";
    return `pill-${status.replace("_", "-")}`;
  };

  return (
    <MainTemplate>
      <Box className="dashboard-wrapper">
        {/* Header Section */}
        <Box className="dashboard-header">
          <Box>
            <Typography variant="h1" className="gradient-title">
              {isRecycleBin ? "Recycle Bin (Deleted Surveys)" : "Surveys & Shipments Overview"}
            </Typography>
            <Typography variant="body1" className="page-subtitle">
              {isRecycleBin
                ? "View, inspect, and restore deleted surveys."
                : "Track logistics flow, client surveys, and move requests in real-time."}
            </Typography>
          </Box>
          
          {/* Segmented Control */}
          <div className="segmented-control-container">
            <div 
              className={`segment-btn ${!isRecycleBin ? 'active' : ''}`}
              onClick={() => {
                setIsRecycleBin(false);
                setCurrentPage(1);
              }}
            >
               <InventoryIcon fontSize="small" color={!isRecycleBin ? "inherit" : "disabled"} />
               Active Surveys
            </div>
            <div 
              className={`segment-btn ${isRecycleBin ? 'active recycle-mode' : ''}`}
              onClick={() => {
                setIsRecycleBin(true);
                setCurrentPage(1);
              }}
            >
               <DeleteIcon fontSize="small"  color={isRecycleBin ? "inherit" : "disabled"} />
               Recycle Bin
            </div>
          </div>
        </Box>

        {/* Filter & Search Bar */}
        <div className="filter-bar">
           <div className="date-input-group">
               <span className="premium-label" style={{ marginBottom: 0 }}>FROM</span>
              <input 
                type="date" 
                className="date-field"
                value={fromDateInput} 
                onChange={(e) => setFromDateInput(e.target.value)} 
              />
           </div>
           
           <div className="filter-separator" />
           
           <div className="date-input-group">
               <span className="premium-label" style={{ marginBottom: 0 }}>TO</span>
              <input 
                type="date" 
                className="date-field"
                value={toDateInput} 
                onChange={(e) => setToDateInput(e.target.value)} 
              />
           </div>

            <Tooltip title="Apply Date Filter">
              <IconButton 
                onClick={handleApplyFilter}
                sx={{ 
                  bgcolor: 'var(--primary-600)', 
                  color: 'var(--white)', 
                  borderRadius: '10px',
                  width: '40px',
                  height: '40px',
                  '&:hover': { bgcolor: 'var(--primary-700)' }
                 }}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
           </Tooltip>

           <div className="filter-separator" />

           {/* Surveyor Filter */}
           <div className="date-input-group" style={{ minWidth: "170px" }}>
              <span className="premium-label" style={{ marginBottom: 0 }}>SURVEYOR</span>
              <select
                className="date-field"
                value={selectedSurveyor}
                onChange={(e) => {
                  setSelectedSurveyor(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ cursor: "pointer", fontWeight: 600, height: "38px" }}
              >
                <option value="ALL">All Surveyors</option>
                <option value="unassigned">Unassigned Only</option>
                {surveyorsList.map((s) => (
                  <option key={s.uuid} value={s.uuid}>
                    {s.name || s.email}
                  </option>
                ))}
              </select>
           </div>

           {(appliedFromDate || appliedToDate || selectedSurveyor !== "ALL") && (
              <Tooltip title="Clear Filters">
                <IconButton 
                  onClick={handleClearFilter}
                  sx={{ 
                    bgcolor: 'var(--gray-100)', 
                    color: 'var(--gray-600)', 
                    borderRadius: '10px',
                    width: '40px',
                    height: '40px',
                    ml: 1,
                    '&:hover': { bgcolor: 'var(--gray-200)', color: 'var(--red-600)' }
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
           )}

           <div style={{ flexGrow: 1 }}></div>

           {/* Search Input */}
           <div className="cosmic-search-wrapper">
            <SearchIcon sx={{ color: 'var(--gray-500)', mr: 2 }} />
            <InputBase
              placeholder="Search by client, email or #"
              fullWidth
               sx={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-main)' }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Stats Grid */}
        {!isRecycleBin && (
          <div className="stats-grid">
            {[
              { label: "Total Shipments", value: totalCount, color: "var(--primary-600)", pct: 100 },
              { label: "New Requests", value: newCount, color: "var(--pink-600)", pct: (newCount/totalCount)*100 },
              { label: "Tasks Assigned", value: assignedCount, color: "var(--blue-600)", pct: (assignedCount/totalCount)*100 },
              { label: "In Progress", value: inprogressCount, color: "var(--yellow-600)", pct: (inprogressCount/totalCount)*100 },
              { label: "Rejected", value: rejectedCount, color: "var(--red-600)", pct: (rejectedCount/totalCount)*100 }
            ].map((stat, i) => (
              <div key={i} className="glass-stat-card">
                <div className="card-icon-blob"></div>
                <div className="stat-content">
                  <div className="stat-fig">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                  <div className="stat-progress-container">
                    <div 
                      className="stat-progress-bar" 
                      style={{ width: `${stat.pct || 0}%`, background: stat.color }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Data Grid Section */}
        <TableContainer component={Paper} className="glass-table-container">
          <Table>
            <TableHead className="glass-table-head">
              <TableRow>
                <TableCell>Client Details</TableCell>
                <TableCell>Contact Point</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Assigned Surveyor</TableCell>
                <TableCell>Logistics Status</TableCell>
                {!isRecycleBin && <TableCell>Workflow</TableCell>}
                <TableCell align="center">Control</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={isRecycleBin ? 6 : 7} align="center" sx={{ py: 12 }}>
                    <CircularProgress size={44} thickness={4} sx={{ color: 'var(--primary-600)' }} />
                  </TableCell>
                </TableRow>
              ) : data.length > 0 ? (
                data.map((row) => (
                  <TableRow 
                    key={row.uuid} 
                    className="glass-table-row clickable-row"
                    onClick={() => goToMoveCheckList(row)}
                  >
                    {/* Client Details */}
                    <TableCell className="glass-cell">
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--text-main)', mb: 0.5 }}>
                        {row.customerName || row.name || row.clientName || (row.surveyDetail && row.surveyDetail.name) || "Unnamed Client"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--gray-500)', fontWeight: 600 }}>
                         #{row.invoiceNo || row.fileNumber || row.fileNo}
                      </Typography>
                    </TableCell>

                    {/* Contact Point */}
                    <TableCell className="glass-cell">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--gray-600)' }}>
                        {row.email || (row.surveyDetail && row.surveyDetail.email) || "-"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--gray-500)', display: 'block' }}>
                        {row.contactNo || row.phone || row.loadPlacePhone || (row.surveyDetail && row.surveyDetail.phone) || "-"}
                      </Typography>
                    </TableCell>

                    {/* Schedule */}
                    <TableCell className="glass-cell">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                        {row.loadDate ? convertDate(row.loadDate) : "TBD"}
                      </Typography>
                    </TableCell>

                    {/* Assigned Surveyor */}
                    <TableCell className="glass-cell" onClick={(e) => e.stopPropagation()}>
                      {row.surveyer || row.surveyerName ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div style={{
                            position: "relative",
                            width: "30px",
                            height: "30px",
                            borderRadius: "50%",
                            overflow: "hidden",
                            background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: 700,
                            boxShadow: "0 2px 5px rgba(2,132,199,0.25)",
                            flexShrink: 0,
                          }}>
                            {row.surveyer?.avatar ? (
                              <img
                                src={getImageUrl(row.surveyer.avatar)}
                                alt=""
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  if (e.currentTarget.nextSibling) {
                                    e.currentTarget.nextSibling.style.display = "flex";
                                  }
                                }}
                              />
                            ) : null}
                            <span style={{ display: row.surveyer?.avatar ? "none" : "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                              {(row.surveyer?.name || row.surveyerName || "S")[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "var(--text-main)", fontSize: "13px" }}>
                              {row.surveyer?.name || row.surveyerName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "var(--gray-500)", display: "block", fontSize: "11px" }}>
                              {row.surveyer?.phone || row.surveyerPhone || row.surveyer?.email || row.surveyerEmail || "Assigned"}
                            </Typography>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          style={{
                            padding: "4px 10px",
                            borderRadius: "6px",
                            background: "#f8fafc",
                            color: "#64748b",
                            fontSize: "11.5px",
                            fontWeight: 600,
                            border: "1px dashed #cbd5e1",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setIdToSend(row.uuid);
                            setShowAssign(true);
                          }}
                          title="Click to assign surveyor"
                        >
                          + Assign Team
                        </button>
                      )}
                    </TableCell>

                    {/* Logistics Status */}
                    <TableCell className="glass-cell">
                      <div className={`glass-pill ${getStatusClass(row.status)}`}>
                        <div className="pill-dot" />
                        {row.status?.replace("_", " ")}
                      </div>
                    </TableCell>

                    {/* Workflow */}
                    {!isRecycleBin && (
                      <TableCell className="glass-cell">
                        <FormControl size="small" variant="standard" fullWidth onClick={(e) => e.stopPropagation()}>
                          <Select
                            disableUnderline
                            value={row.status}
                            onChange={(e) => handleStatus(e, row.uuid)}
                            className="manage-action-btn"
                            renderValue={() => "Manage Shipment"}
                          >
                            <MenuItem disabled value={row.status}>Pipeline Actions</MenuItem>
                            {row.status === "new" && [
                              <MenuItem key="a" value="assigned" sx={{ fontWeight: 600 }}>Assign Team</MenuItem>,
                              <MenuItem key="r" value="rejected" sx={{ color: '#ef4444', fontWeight: 600 }}>Reject Order</MenuItem>
                            ]}
                            {row.status === "assigned" && [
                              <MenuItem key="u" value="un_assign" sx={{ fontWeight: 600 }}>Release Team</MenuItem>,
                              <MenuItem key="r" value="rejected" sx={{ color: '#ef4444', fontWeight: 600 }}>Cancel Shipment</MenuItem>
                            ]}
                             {(row.status === "rejected" || row.status === "cancelled") && <MenuItem value="assigned" sx={{ fontWeight: 600 }}>Revive & Assign</MenuItem>}
                            {row.status === "incomplete_info" && <MenuItem value="complete" sx={{ fontWeight: 600 }}>Finalize Info</MenuItem>}
                          </Select>
                        </FormControl>
                      </TableCell>
                    )}

                    {/* Control */}
                    <TableCell align="center" className="glass-cell" onClick={(e) => e.stopPropagation()}>
                      {isRecycleBin ? (
                        <Tooltip title="Restore from Bin">
                          <IconButton onClick={() => handleRestore(row.uuid)} className="glass-op-btn op-btn-restore">
                            <RestoreFromTrashIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Move to Bin">
                          <IconButton 
                            onClick={() => initiateDelete(row)} 
                            className="glass-op-btn op-btn-delete"
                            disabled={isEditLoading}
                          >
                            {isEditLoading && pendingAction?.id === row.uuid ? 
                              <CircularProgress size={18} color="inherit" /> : 
                              <DeleteIcon fontSize="small" />
                            }
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isRecycleBin ? 6 : 7} align="center" sx={{ py: 15 }}>
                    <Box sx={{ opacity: 0.5 }}>
                      <InventoryIcon sx={{ fontSize: 64, mb: 2, color: '#94a3b8' }} />
                      <Typography variant="h6" fontWeight="700">
                        {isRecycleBin ? "Recycle Bin is Empty" : "No Operations Found"}
                      </Typography>
                      <Typography variant="body2">
                        {isRecycleBin 
                          ? "There are no deleted surveys in the recycle bin." 
                          : "The logistics pipeline is currently empty."}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dynamic Pagination Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={5}>
          <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 600 }}>
             Operational records: {itemsPerPage * (currentPage - 1) + 1} - {Math.min(itemsPerPage * currentPage, totalCount)} of {totalCount}
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            variant="text"
            shape="rounded"
            size="large"
          />
        </Box>
      </Box>

      {/* Logic Modals (Invisible Layer) */}
      <AssignSurveyorModal
        open={showAssign}
        onClose={() => setShowAssign(false)}
        onSucces={(val) => {
          setEmail(val);
          setSuccessHead("Assignment Confirmed");
          setSuccessSub("Team successfully dispatched to handle shipment.");
          setShowAssign(false);
          setSuccess(true);
          getAllShipments();
        }}
        id={idToSend}
      />

      <RejectModal
        open={reject}
        onClose={() => setReject(false)}
        onSucces={() => {
          setEmail("");
          setReject(false);
          setSuccess(true);
          setSuccessHead("Shipment Rejected");
          setSuccessSub("The shipment has been moved to the rejection queue.");
          getAllShipments();
        }}
        id={idToSend}
      />

      <SuccessModal
        open={success}
        onClose={() => setSuccess(false)}
        email={email}
        head={successHead}
        sub={successSub}
      />

      <SuperAdminVerifationModal
        open={isVerified}
        onClose={() => setIsVerified(false)}
        onSuccess={handleDeleteSuccess}
        onError={() => {
          setSnackbarMessage("Auth Code Verification Failed");
          setOpenSnackbar(true);
        }}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert 
          elevation={12} 
          variant="filled" 
          severity={snackbarMessage.toLowerCase().includes("failed") || snackbarMessage.toLowerCase().includes("error") ? "error" : "success"} 
          onClose={handleCloseSnackbar}
          sx={{ borderRadius: '12px', fontWeight: 600 }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </MainTemplate>
  );
};

export default Dashboard;
